import './bootstrap';
import Kibana from './utils/trackKibana.js';
import AppConstructor from '#/App/App.jsx';
import HostGateway from '#/services/host-gateway.js';
import * as actions from '#/store/actions/actions.js';
import scopeLogger from '#/lib/scope-logger.js';
import * as hooks from '#/hooks/index.js';
import ConfigProvider from '#/providers/config/config.js';
import CreativeProvider from '#/providers/creative/index.js';
import createStore from '#/store/store.js';
import InteractiveApi from '#/services/interactive-api.js';
import RenderersRegistry from '#/providers/events/renderers-registry.js';
import EventsProvider from '#/providers/events/events-provider.js';
import MNS from '#/services/MNS/index.js';
import { preloadImages } from '#/utils/preloadAssets.js';
import interpolate from '#/lib/interpolate.js';
import connectGateways from '#/connectGateways.js';
import InterpolationAPI from '#/services/interpolation-api.js';
import SessionStorage from '#/services/session-storage.js';
import ViewportMatcher from '#/services/viewport-matcher.ts';
import { BehaviorSubject, filter, map, tap } from 'rxjs';
import { MetricsCollector } from '#/services/metric-collector.js';

const kibana = new Kibana('entry');

// just for this scope
kibana.setLevel('log');

try {
  const hostGateway = new HostGateway();
  const session = new SessionStorage();
  const configProvider = new ConfigProvider({ session });

  const viewportMatcher = new ViewportMatcher(configProvider);


  const mns = new MNS(configProvider);
  const interactiveAPI = new InteractiveApi(configProvider, (error) => {
    if (error.status === 401) {
        hostGateway.requestNewToken();

        /* Returning something indicate that recovery is possible,
         delay property with false indicate that recovery should await for update tokens */
        return {
          delay: false,
        }
      }
    });

  const creativeProvider = new CreativeProvider();

  const preloadedState = session.get('store');
  session.remove('store');

  const store = createStore(
    {
      preloadedState,
    },
    {
      // TODO: Due to upcoming RMI-1913 merging it seems that interactiveAPI
      //  does not need in store anymore, so we can **try** remove it from the store.
      //  Just before the next "full regression" small changes.
      interactiveAPI,
    },
  );

  connectGateways({
    hostGateway,
    mns,
    store,
    configProvider,
    interactiveAPI,
  });
  hostGateway.sendNotify.loaded();

  const renderersRegistry = new RenderersRegistry();


  /**
   * @todo Typing!!!
   * @name InternalAPI
   * @type {{mns: {subscribe: (function(*): function(): Promise<{}>|Promise<unknown>), unsubscribe: ((function(*, {}=): (Promise<{}>))|*), publish: internalAPI.mns.publish, readonly message$: Subject<T>}, hostApp: {removeControl(*): void, requestForeground({onCloseRequest: *, mode?: string}=): void, releaseForeground(): void, readonly message$: Subject<T>, createControl(*): void}, interpolate(*, *): *, getObjectConfig: ((function(*): Promise<*>)|*), readonly timeService: BehaviorSubject<number>}}
   */
  const internalAPI = {
    renderersRegistry,
    configProvider,
    store,
    interactiveAPI,
    /**
     * @deprecated in prior to direct usage 'interactiveAPI.getConfig'
     */
    getObjectConfig: interactiveAPI.getConfig,
    // creativesAPI,
    get assetService() {
      return {
        preload: preloadImages,
      };
    },
    /**
     *
     * @param {Extension} extension
     * @return {any}
     */
    hostApp: hostGateway,
    panic(reason) {
      // TODO: send error notify to host app
      // TODO: send error to log.cent.re
      console.error('panic', reason);
    },
    mns: {
      get message$() {
        return mns.message$;
      },
      publish: (topic, message, qos, ts) => {
        mns.publish(topic, {
          ts: ts ?? hostGateway.position,
          data: message,
        }, qos);
      },
      subscribe: (topic) => {
        mns.subscribe(topic);
        return () => mns.unsubscribe(topic);
      },
      unsubscribe: mns.unsubscribe,
    },
    /**
     * @deprecated moved to 'interpolation' props as 'replace'
     */
    interpolate(templateString, parameters) {
      return interpolate(templateString, parameters);
    },
    interpolation: new InterpolationAPI({store}),
    /**
     * @deprecated in prior to 'hostGateway.position$'
     */
    get timeService() {
      console.warn('DEPRECATED: use hostGateway.position$');
      return hostGateway.position$;
    },
    viewportMatcher,
  };

  const metricCollector = new MetricsCollector({
    configProvider,
    mns,
    store,
  });

  new EventsProvider({
    mns,
    store,
    hostGateway,
    creativeProvider,
    renderersRegistry,
    viewportMatcher,
    configProvider,
    metricCollector,
    /* TODO: do we should inject the full internal API? +1*/
  });

  const app = AppConstructor(internalAPI, {session, configProvider});
  const container = document.getElementById('root');
  app.render(container);

  {
    /**
     * This is the one and single entry point for enable ET logger,
     * all necessary dependency, parameters, and so on MUST by injected here.
     * No any other place is not allowed!!!
     */
    const currentObject = new BehaviorSubject(null);
    hostGateway.message$
      .pipe(
        filter(({ cmd }) => cmd === 'start'),
        map(({ payload }) => payload),
      )
      .subscribe(currentObject);

    import('./lib/et/et.js').then(({ default: ET }) => {
      const et = new ET({
        root: container,
        hostGateway,
      });
      et.start();
      currentObject.subscribe((object) => {
        if (!object) {
          return;
        }
        et.setTarget(object);
      });
    });
  }

  exposePublicAPI(internalAPI);


  // Here we really start to work

  /* it is full loading from scratch, instead partial self-reload (hot-reload, change backend, frontend, etc.) */
  if (!preloadedState) {
    const subscription = hostGateway.message$
      .pipe(
        filter(({ cmd }) => cmd === 'create'),
        tap(({ payload }) => {
          configProvider.newConfig(payload);
        })
      )
      .subscribe(async ({ payload }) => {
        const enabledExtensions = (payload.mns_config.extensions ?? [])
          .filter(({ allowed }) => allowed)
          .map(({ name }) => name);

        kibana.log('start', { extensions: enabledExtensions });

        subscription.unsubscribe();
      });

    hostGateway.sendNotify.ready();
  } else {
    const currentObject = store.getState().video.description;
    store.dispatch(actions.interactiveStart(currentObject));
  }

} catch (error) {
  console.error('Error during app initialization', error);
  kibana.error(error);
}

function exposePublicAPI(internalAPI) {
  /* "public" API */
  window.Megogo = window.Megogo || {};
  window.Megogo.scopeLogger = scopeLogger;
  window.Megogo.interactive = {
    info() {
      const _info = [
        `Version ${import.meta.env.VITE_APP_VERSION}-${
          import.meta.env.VITE_TARGET_PLATFORM
        }`,
        `Build on ${import.meta.env.VITE_BUILD_LABEL} at ${
          import.meta.env.VITE_BUILD_DATE
        }.`,
        `Changelog: ${window.__change_log__.href}`,
        // `Bridge: ${actions.version}`,
      ];
      console.groupCollapsed(
        '%cMegogo interactive.',
        'background-color: black; color: #00B3B3; size: 1.2em; padding: 0.3em 2em; font-weight: bold;',
      );
      console.info(_info.join('\n'));
      console.groupEnd();
    },
    hooks,
  };

  window.Megogo.mnsMessages$ = internalAPI.mns.message$;
  Object.defineProperty(window.Megogo.interactive, 'state', {
    get() {
      return internalAPI.store.getState();
    },
    configurable: true,
  });

  window.Megogo.interactive.API ??= {};
  Object.defineProperty(window.Megogo.interactive.API, 'debug', {
    get() {
      return internalAPI.store.getState().app.debug;
    },
    set(enabled) {
      internalAPI.store.dispatch(actions.setDebug(enabled));
    },
  });

  window.Megogo.interactive.API.debug = import.meta.env.DEV;
}

/*
If you want your app to work offline and load faster, you can change
unregister() to register() below. Note this comes with some pitfalls.
Learn more about service workers: https://bit.ly/CRA-PWA
  // serviceWorker.unregister();
*/
